/**
 * Video embed resolver.
 *
 * Turns a URL an editor pasted into a player embed. Shared by the landing
 * page's modal and the inline player on tutorial articles so both accept
 * exactly the same set of links.
 */

export type EmbedKind = 'youtube' | 'vimeo' | 'video' | 'iframe';

export interface Embed {
  kind: EmbedKind;
  src: string;
}

export function getEmbed(url: string): Embed | null {
  const u = url.trim();
  if (!u) return null;

  // Vimeo — plain links, share links with query params, and player links.
  const vimeo = u.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeo) {
    return { kind: 'vimeo', src: `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1&dnt=1` };
  }

  // YouTube — both watch links and short youtu.be links.
  const yt = u.match(
    /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([\w-]{6,})/i
  );
  if (yt) {
    const params = new URLSearchParams({ autoplay: '1', rel: '0', modestbranding: '1', playsinline: '1' });
    return { kind: 'youtube', src: `https://www.youtube.com/embed/${yt[1]}?${params}` };
  }

  // Direct video file.
  if (/\.(mp4|webm|ogv|mov)(\?.*)?$/i.test(u)) {
    return { kind: 'video', src: u };
  }

  // Fall back: try to embed via iframe.
  return { kind: 'iframe', src: u };
}

export function renderFrame(embed: Embed): string {
  if (embed.kind === 'video') {
    return `<video src="${embed.src}" controls autoplay playsinline></video>`;
  }
  return `<iframe
    src="${embed.src}"
    allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
    allowfullscreen
    referrerpolicy="strict-origin-when-cross-origin"
    loading="eager"
  ></iframe>`;
}
